import React, { useEffect, useState } from 'react'
import { Redirect, useHistory, useLocation } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import Header from '../../components/FormTools/FormHeader'
import { CheckList, Form, SpinLoading } from 'antd-mobile-v5'
import UploadOssFile from '../../components/FormTools/UploadOssFile'
import { useGetOssSignature, useGetOtherAppsTypeList, useSubmitOtherApps } from '../../services/hooks'
import { useLanguage } from '../../services/hooks/user'
import { removeExpires } from '../../components/common/utils'
import { getOtherAppsPaymentStatus } from '../../services/api'
import Status from './Status'
import AutoRenewCheckbox from '../../components/common/AutoRenewCheckbox'
import { WhiteSpace } from 'antd-mobile'

const PocketsApplication = () => {
  const lang = useLanguage()
  const { push } = useHistory()
  const { formatMessage } = useIntl()
  const { state } = useLocation()
  // detail 只能经 history.state 传入（无详情接口）；直接访问 URL 时 state 缺失，兜底回列表页防白屏
  const initialDetail = state && state.detail
  const [detail, setDetail] = useState(initialDetail)
  const [form] = Form.useForm()

  // 支付成功后整页 reload，history.state 仍是进入页面时的旧快照（paymentMethod 未写入），
  // 会残留"待支付"状态与支付按钮。进入页面时按 code 调 paymentStatus 自愈拉最新
  // 支付/业务状态合入（与 FuiouQrPopup 支付成功判定同源端点）。
  // 仅 mount 时执行一次：effect 内 setDetail 不改 code，不触发重跑
  useEffect(() => {
    if (!initialDetail || !initialDetail.code || initialDetail.paymentMethod || initialDetail.paid) return
    let cancelled = false
    getOtherAppsPaymentStatus(initialDetail.code).then(({ data, error }) => {
      if (cancelled || error || !data) return
      setDetail((prev) => ({
        ...prev,
        paymentMethod: data.paymentMethod || prev.paymentMethod,
        // 申请类 paymentStatus 的 status 是业务状态（如支付成功后 WAIT_ADMIN_APPROVED），优先采用
        status: data.status || prev.status,
        paid: data.paid != null ? data.paid : prev.paid,
      }))
    })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const required = true
  const { data: ossSignature } = useGetOssSignature('OTHER')
  const { data: appTypes, loading: typeLoading } = useGetOtherAppsTypeList(true)
  const { runAsync: submit, loading: submitting } = useSubmitOtherApps()
  const [isAutoRenewChecked, setIsAutoRenewChecked] = useState(false)
  const [isAutoRenewJump, setIsAutoRenewJump] = useState(false)

  useEffect(() => {
    if (detail) form.setFieldsValue({ files: detail.files, memberApplicationType: [detail.memberApplicationType] })
  }, [detail])

  const filename = (dir, type) => `${dir}${type}/`

  const changeFiles = (files) => {
    form.setFieldsValue({ files })
  }

  const onFinish = (values) => {
    const data = { memberApplicationType: values.memberApplicationType[0], files: removeExpires(values.files) }
    if (detail && detail.code) data.code = detail.code
    submit(data).then((res) => {
      if (res.code) push('/pockets_apps_result', { detail: res, isAutoRenewJump })
    })
  }

  const isFormEditable = () => {
    if (!detail || (detail && detail.status === 'ADMIN_NOT_APPROVED')) return true
    else return false
  }

  const handleJumpChange = (isAutoRenewJump) => {
    setIsAutoRenewJump(isAutoRenewJump)
  }

  // state 缺失（直接访问 URL/外部跳转）无上下文：回列表页。
  // 注意 detail 为 null 是"创建新申请"的合法形态（列表页加号 push 时显式传 null），不能拦截
  if (!state) return <Redirect to="/pockets_apps_list" />

  return (
    <>
      <Header text={<FormattedMessage id="pocket-apps-title" />} code={detail?.code} />
      <Form disabled={submitting} form={form} validateTrigger="onSubmit">
        <Form.Item
          name="memberApplicationType"
          label={formatMessage({ id: 'pocket-apps-type' })}
          rules={
            required && [
              {
                required: true,
                message: formatMessage({ id: 'pocket-apps-type-error' }),
              },
            ]
          }
        >
          {typeLoading ? (
            <SpinLoading color="primary" />
          ) : (
            <CheckList readOnly={!isFormEditable()}>
              {appTypes &&
                appTypes.map((type) => (
                  <CheckList.Item value={type.type} key={type.id}>
                    {type['name_' + lang]}
                  </CheckList.Item>
                ))}
            </CheckList>
          )}
        </Form.Item>
        {ossSignature && (
          <Form.Item
            name="files"
            label={formatMessage({ id: 'si-upload-btn' })}
            form={form}
            rules={
              required && [
                {
                  required: true,
                  message: formatMessage({ id: 'form-upload-error' }),
                },
              ]
            }
          >
            <UploadOssFile
              form={form}
              fileName={filename(ossSignature.dir, 'other')}
              ossSignature={ossSignature}
              onSuccess={changeFiles}
              editable={isFormEditable()}
              fileList={detail ? detail.files : null}
            />
          </Form.Item>
        )}
        {isFormEditable() && (
          <AutoRenewCheckbox
            setChecked={setIsAutoRenewChecked}
            checked={isAutoRenewChecked}
            setJump={handleJumpChange}
            jump={isAutoRenewJump}
          />
        )}
        <WhiteSpace size="xl" />
        <Status
          status={detail?.status}
          isAutoRenewJump={isAutoRenewJump}
          form={form}
          paymentMethod={detail?.paymentMethod}
          code={detail?.code}
          applicationType={detail?.memberApplicationType}
          submitting={submitting}
          note={detail?.note}
          finishFn={onFinish}
        />
      </Form>
    </>
  )
}

export default PocketsApplication
